import type { MetaHeadProps } from './types';
import type { ReactElement } from 'react';

import { BaseMetaHead } from './BaseMetaHead';
import { SCHEMA_CONTEXT, schemaType } from '../../../utilities/constants/schemaTypes';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';

export const HomepageMetaHead = ({ metadataContent }: MetaHeadProps): ReactElement => {
  const { locale, productionHost } = useAppContext();
  const {
    configuration: { appStoreName },
  } = useStaticContext();

  const metaTitle = metadataContent?.pageTitle || '';
  const pageUrl = `${productionHost}/${locale}`;

  const structuredData = metadataContent?.structuredData?.content && {
    '@context': SCHEMA_CONTEXT,
    '@type': schemaType.organization,
    ...Object.fromEntries(
      Object.entries(metadataContent.structuredData.content).filter(([key]) => key !== '_meta')
    ),
    address: {
      '@type': schemaType.postalAddress,
      ...(metadataContent?.structuredData?.content?.address ?? {}),
    },
  };

  return (
    <BaseMetaHead
      title={metaTitle}
      description={metadataContent?.pageDescription || ''}
      robots={metadataContent?.robots || 'index,follow'}
      canonicalUrl={pageUrl}
      metaTags={metadataContent?.metaTags}
      iosDeepLink={`${appStoreName}://home`}
      universalLinksRequireGStar={false}
      alwaysRenderSiteName
      ogImageChildren={
        metadataContent?.ogImageUrl && (
          <>
            <meta
              key="meta-og:image"
              property="og:image"
              content={metadataContent?.ogImageUrl || ''}
            />
            <meta key="meta-og:image:width" property="og:image:width" content="2000" />
            <meta key="meta-og:image:height" property="og:image:height" content="2000" />
          </>
        )
      }
      trailingChildren={
        structuredData && (
          <script
            id="homepage-structured-data"
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )
      }
    >
      <meta key="meta-og:title" property="og:title" content={metaTitle} />
      <meta key="meta-og:type" property="og:type" content="website" />
      <meta key="meta-og:url" property="og:url" content={pageUrl} />
      {metadataContent?.pageDescription && (
        <meta
          key="meta-og:description"
          property="og:description"
          content={metadataContent?.pageDescription || ''}
        />
      )}
    </BaseMetaHead>
  );
};
